import { useCallback, useRef, useState } from "react";

const CANDIDATE_MIME_TYPES = [
  "audio/mp4",
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/ogg;codecs=opus",
];

function pickSupportedMimeType(): string | undefined {
  if (typeof MediaRecorder === "undefined") return undefined;
  return CANDIDATE_MIME_TYPES.find((type) => MediaRecorder.isTypeSupported(type));
}

interface UseAudioRecorderResult {
  isRecording: boolean;
  recordingSeconds: number;
  start: (stream: MediaStream) => void;
  stop: () => Promise<{ blob: Blob; mimeType: string } | null>;
  cancel: () => void;
}

/**
 * Records from a MediaStream it is handed (ownership of the stream itself —
 * acquiring/releasing the microphone — belongs to useMicStream). This lets
 * the same open mic stream be recorded from repeatedly across hands-free
 * turns without re-requesting getUserMedia each time.
 */
export function useAudioRecorder(): UseAudioRecorderResult {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const mimeTypeRef = useRef<string>("audio/webm");
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const cancelledRef = useRef(false);

  const stopTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const start = useCallback((stream: MediaStream) => {
    const mimeType = pickSupportedMimeType();
    if (!mimeType) {
      throw new Error("Prehliadač nepodporuje žiadny vhodný audio formát.");
    }
    mimeTypeRef.current = mimeType;
    cancelledRef.current = false;
    chunksRef.current = [];
    setRecordingSeconds(0);

    const recorder = new MediaRecorder(stream, { mimeType });
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunksRef.current.push(event.data);
    };
    mediaRecorderRef.current = recorder;
    recorder.start();
    setIsRecording(true);

    timerRef.current = setInterval(() => {
      setRecordingSeconds((seconds) => seconds + 1);
    }, 1000);
  }, []);

  const stop = useCallback((): Promise<{ blob: Blob; mimeType: string } | null> => {
    return new Promise((resolve) => {
      const recorder = mediaRecorderRef.current;
      if (!recorder || recorder.state === "inactive") {
        resolve(null);
        return;
      }

      recorder.onstop = () => {
        stopTimer();
        setIsRecording(false);
        if (cancelledRef.current) {
          resolve(null);
          return;
        }
        const blob = new Blob(chunksRef.current, { type: mimeTypeRef.current });
        resolve(blob.size > 0 ? { blob, mimeType: mimeTypeRef.current } : null);
      };

      recorder.stop();
    });
  }, [stopTimer]);

  const cancel = useCallback(() => {
    cancelledRef.current = true;
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.stop();
    } else {
      stopTimer();
      setIsRecording(false);
    }
  }, [stopTimer]);

  return { isRecording, recordingSeconds, start, stop, cancel };
}
